import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@finora/api-client";
import { Button } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

export function IntesaCardTab() {
  const queryClient = useQueryClient();
  const {
    data: settings,
    isLoading,
    isError: isSettingsError,
    error: settingsError,
  } = useQuery({
    queryKey: ["intesa-card-settings"],
    queryFn: () => api.getIntesaCardSettings(),
    // Isti razlog kao OtpCardTab - ako modul "intesa_card" nije aktivan za
    // firmu (RequireModule guard na backendu vraća 403), nema smisla
    // ponavljati zahtev.
    retry: false,
  });

  const [enabled, setEnabled] = useState(false);
  const [environment, setEnvironment] = useState<"TEST" | "PRODUCTION">("TEST");
  const [clientId, setClientId] = useState("");
  const [currency, setCurrency] = useState("941");
  const [storeKey, setStoreKey] = useState("");

  useEffect(() => {
    if (settings) {
      setEnabled(settings.intesaCardEnabled);
      setEnvironment(settings.intesaCardEnvironment);
      setClientId(settings.intesaCardClientId ?? "");
      setCurrency(settings.intesaCardCurrency);
    }
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      api.updateIntesaCardSettings({
        intesaCardEnabled: enabled,
        intesaCardEnvironment: environment,
        intesaCardClientId: clientId || undefined,
        intesaCardCurrency: currency || undefined,
        intesaCardStoreKey: storeKey || undefined,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(["intesa-card-settings"], updated);
      setStoreKey("");
    },
  });

  if (isSettingsError) {
    if (settingsError instanceof ApiError && settingsError.response.errorCode === "MODULE_NOT_AVAILABLE") {
      return <ModuleGateNotice error={settingsError} moduleKey="intesa_card" />;
    }
    return (
      <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
        {settingsError instanceof Error ? settingsError.message : "Učitavanje nije uspelo."}
      </p>
    );
  }

  if (isLoading || !settings) {
    return <p className="text-sm text-slate-500">Učitavanje...</p>;
  }

  return (
    <div className="max-w-xl space-y-5">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">Kartice - Banca Intesa</h2>
        <p className="text-xs text-slate-400">
          Naplata faktura platnim karticama preko Banca Intesa Beograd (NestPay "3D Pay Hosting",
          operator Mercury Processing Services International). Kupac se preusmerava na hostovanu
          stranicu banke (unos podataka kartice, 3D Secure), pa se vraća nazad na fakturu sa
          ishodom. Client ID i Store Key dobijaš u NestPay Merchant Center-u uz ugovor o
          prihvatanju kartica.
        </p>
        <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Napomena: ako prodaješ fizičku robu koja se isporučuje kasnije, Banca Intesa po svom
          uputstvu (EPM) zahteva model predautorizacije sa naknadnom realizacijom (ne automatsku
          naplatu) - trenutno je u ovoj verziji uključena automatska naplata (Auth). Javi se ako
          treba promeniti model.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Aktivna naplata karticama (Banca Intesa) za ovu firmu
      </label>

      <div>
        <label className="block text-xs font-medium text-slate-500">Okruženje</label>
        <select
          value={environment}
          onChange={(e) => setEnvironment(e.target.value as "TEST" | "PRODUCTION")}
          className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        >
          <option value="TEST">Test (testsecurepay.eway2pay.com)</option>
          <option value="PRODUCTION">Produkcija (bib.eway2pay.com)</option>
        </select>
        <p className="mt-1 text-xs text-slate-400">
          Ako menjaš iz test u produkcijsko okruženje (ili obrnuto), obavezno promeni i Client ID i
          Store Key - banka ih izdaje odvojeno po okruženju.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500">Client ID (Merchant ID)</label>
          <input
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="npr. 1753019"
            className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500">Šifra valute</label>
          <input
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            placeholder="941 (RSD)"
            className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500">Store Key</label>
        <input
          value={storeKey}
          onChange={(e) => setStoreKey(e.target.value)}
          type="password"
          placeholder={settings.hasIntesaCardStoreKey ? "•••••••••• (nepromenjeno)" : ""}
          className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        />
        {settings.hasIntesaCardStoreKey && (
          <p className="mt-1 text-xs text-slate-400">Trenutno je unet ključ - ostavi prazno da ga zadržiš.</p>
        )}
        <p className="mt-1 text-xs text-slate-400">
          Store Key se nalazi u NestPay Merchant Center-u (Administration → Change Store Key →
          Old Store Key).
        </p>
      </div>

      {save.isError && save.error instanceof ApiError && save.error.response.errorCode === "MODULE_NOT_AVAILABLE" ? (
        <ModuleGateNotice error={save.error} moduleKey="intesa_card" />
      ) : (
        save.isError && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {save.error instanceof Error ? save.error.message : "Čuvanje nije uspelo."}
          </p>
        )
      )}
      {save.isSuccess && !save.isPending && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">Sačuvano.</p>
      )}

      <Button disabled={save.isPending} onClick={() => save.mutate()}>
        Sačuvaj
      </Button>
    </div>
  );
}
